import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CircleNotch, Images } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

// Admin-only: flatten the current editor design into an image and save it into the
// Styles Library (Looks) as a new reference style. Reuses POST /api/library/styles (multipart, admin-gated).
export default function SaveToStylesModal({ open, onOpenChange, defaultTitle, capture }) {
  const [tax, setTax] = useState([]);
  const [cat, setCat] = useState("");
  const [sub, setSub] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/library/taxonomy").then((r) => {
      const cats = r.data?.categories || [];
      setTax(cats);
      if (cats.length) { setCat(cats[0].name); setSub(cats[0].subcategories?.[0]?.name || ""); }
    }).catch(() => {});
    setTitle(defaultTitle || "");
  }, [open, defaultTitle]);

  const subs = tax.find((c) => c.name === cat)?.subcategories || [];
  useEffect(() => {
    if (subs.length && !subs.some((s) => s.name === sub)) setSub(subs[0].name);
  }, [cat]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!cat || !sub) { toast.error("Pick a category and subcategory."); return; }
    if (!title.trim()) { toast.error("Give the style a title."); return; }
    setSaving(true);
    try {
      const dataUrl = await capture();
      if (!dataUrl) throw new Error("capture-failed");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `${(title.trim() || "style").replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.png`, { type: blob.type || "image/png" });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", cat);
      fd.append("subcategory", sub);
      fd.append("title", title.trim());
      await api.post("/library/styles", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Saved to Styles → ${cat} / ${sub}.`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save to Styles. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="editor-save-styles-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Images size={20} weight="fill" className="text-fuchsia-400" /> Save to Styles Library
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-1">Flattens this design into a reference image for the pick-a-look workflow.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">Category</label>
            <select data-testid="save-styles-category" value={cat} onChange={(e) => setCat(e.target.value)} className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60">
              {tax.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">Subcategory</label>
            <select data-testid="save-styles-subcategory" value={sub} onChange={(e) => setSub(e.target.value)} className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60">
              {subs.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-1">
          <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">Title</label>
          <input data-testid="save-styles-title" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} placeholder="e.g. Neon Midnight Party" className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60" />
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-full text-sm font-medium text-zinc-400 hover:text-white transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-styles-submit" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-bold transition-colors disabled:opacity-60">
            {saving ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <>Save to Styles</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
